import classnames from 'classnames';
import { forwardRef, useEffect, useState } from 'react';

import { DocumentOutline } from '@/core/tamagoshi/icons/DocumentOutline';
import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { isB2C } from '@/domains/core/settings/utils';
import { H3 } from '@/domains/core/tamagoshiv2/components/Typography/Typography';
import { getSmartDescription } from '@/domains/productDiscovery/ProductPage/common/components/Description/smartDescription.api';
import { descriptionTitle } from '@/domains/productDiscovery/ProductPage/common/components/Description/translations';

import styles from './Description.module.scss';

interface DescriptionProps {
  id?: string;
  isExpanded: boolean;
  description?: string;
  modelId: number;
  hideTitle?: boolean;
}

export const Description = forwardRef<HTMLDivElement, DescriptionProps>(
  (
    {
      id,
      isExpanded,
      description: originDescription,
      modelId,
      hideTitle = false,
    },
    ref,
  ) => {
    const descriptionClassName = classnames(styles.description, {
      [styles.expanded]: isExpanded,
      [styles.folded]: !isExpanded,
    });

    const [description, setDescription] = useState<string | undefined>(
      originDescription,
    );

    const [enableSmartDescription] = useBooleanFlags(['smartDescriptionV2']);

    useEffect(() => {
      async function fetchSmartDescription() {
        const smartDescription = await getSmartDescription(modelId);
        if (smartDescription) {
          setDescription(smartDescription);
        }
      }

      if (enableSmartDescription) {
        fetchSmartDescription();
      } else {
        setDescription(originDescription);
      }
    }, [enableSmartDescription, modelId, originDescription]);

    if (!description) return null;

    return (
      <div
        id={id}
        className={classnames(styles.descriptionContainer, 'rich-text-block')}
        ref={ref}
      >
        {!hideTitle && (
          <H3 className={styles.descriptionTitle}>
            {descriptionTitle}
            {isB2C() && <DocumentOutline className={styles.icon} />}
          </H3>
        )}
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: description }}
          className={descriptionClassName}
          data-testid="description-content"
        />
      </div>
    );
  },
);

Description.displayName = 'Description';
